<template>
  <!-- Main navigation drawer component -->
  <q-drawer
    show-if-above
    v-model="showMenuLocal"
    no-swipe-backdrop
    no-swipe-close
    no-swipe-open
    behavior="mobile"
    side="left"
    bordered
    content-class="bg-grey-1"
    :width="300"
    @click="leftDrawerOpen"
    @input="onDrawerToggle"
  >
    <!-- Fixed header section -->
    <div class="drawer-header">
      <q-btn flat dense round color="secondary" @click="closeDrawer" aria-label="Menu" icon="menu" class="navbar__drawerBtn" />
      <img class="drawer-logo" src="@/assets/logo.png" />
    </div>

    <!-- Scrollable area for menu items only -->
    <q-scroll-area class="drawer-content">
      <q-list padding class="navDrawer__list" align="left">
        <!-- Dashboard (shown for admins, agency clients, B2C users, and sub-clients) -->
        <template v-if="shouldShowDashboard">
          <q-item class="navDrawer__drawer-item" clickable v-ripple :active="isActive('home')" @click.native="handleRoute('home')">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon color="grey-7" name="o_space_dashboard" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary" style="font-size: 16px">{{ $t('Dashboard') }}</q-item-label>
              </q-item-section>
            </div>
          </q-item>
        </template>

        <!-- CREATE & EDIT Section - shown when user has access to any create/edit features -->
        <template v-if="isAdmin || isClient || isSubClient || isB2C">
          <div class="section-header">CREATE & EDIT</div>
        </template>

        <!-- Orders/Shoots menu item -->
        <template v-if="!isClient || hasShoot">
          <q-item class="navDrawer__drawer-item" clickable :active="isActive('orders')" @click.native="handleRoute('orders')">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon color="grey-7" name="o_calendar_today" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary" style="font-size: 16px">Bookings</q-item-label>
              </q-item-section>
            </div>
          </q-item>
        </template>

        <!-- AI Editor menu item - for clients and admins who can edit -->
        <template v-if="isClient || isAdmin || isSubClient || isB2C">
          <q-item class="navDrawer__drawer-item" clickable @click.native="handleAIEditorRoute">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon color="grey-7" name="o_auto_fix_high" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary" style="font-size: 16px">{{ $t('AI Editor') }}</q-item-label>
              </q-item-section>
            </div>
          </q-item>
        </template>

        <!-- Video Generator menu item -->
        <template v-if="isAdmin || isClient || isSubClient || isB2C">
          <q-item class="navDrawer__drawer-item" clickable @click.native="handleVideoGeneratorRoute">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon color="grey-7" name="o_movie" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary" style="font-size: 16px">{{ $t('Video Generator') }}</q-item-label>
              </q-item-section>
            </div>
          </q-item>
        </template>

        <!-- Templates menu item - UPCOMING FEATURE -->
        <template v-if="isClient || isAdmin || isSubClient || isB2C">
          <q-item class="navDrawer__drawer-item navDrawer__drawer-item--disabled" @mouseover="showFeatureTooltip('templates', $event)" @mouseleave="hideFeatureTooltip">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon class="navDrawer__disabled-icon" name="o_dashboard_customize" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="navDrawer__disabled-text" style="font-size: 16px">Templates</q-item-label>
              </q-item-section>
            </div>
          </q-item>
        </template>

        <!-- MANAGE Section - shown when user has access to any management features -->
        <template v-if="isClient || isAdmin || isSubClient || isB2C">
          <div class="section-header">MANAGE</div>
        </template>

        <!-- Smart Library menu item - available to all client types -->
        <template v-if="isClient || isAdmin || isSubClient || isB2C">
          <q-item class="navDrawer__drawer-item" clickable :active="isActive('gallery')" @click.native="handleRoute('gallery')">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon color="grey-7" name="o_collections" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary" style="font-size: 16px">Smart Library</q-item-label>
              </q-item-section>
            </div>
          </q-item>
        </template>

        <!-- Collections menu item -->
        <template v-if="isClient || isAdmin || isSubClient || isB2C">
          <q-item class="navDrawer__drawer-item" clickable>
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon color="grey-7" name="o_folder_special" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary" style="font-size: 16px">Collections</q-item-label>
              </q-item-section>
            </div>
          </q-item>
        </template>

        <!-- Brand Kits menu item - UPCOMING FEATURE -->
        <template v-if="isClient || isAdmin || isSubClient || isB2C">
          <q-item class="navDrawer__drawer-item navDrawer__drawer-item--disabled" @mouseover="showFeatureTooltip('brandKits', $event)" @mouseleave="hideFeatureTooltip">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon class="navDrawer__disabled-icon" name="o_palette" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="navDrawer__disabled-text" style="font-size: 16px">Brand Kits</q-item-label>
              </q-item-section>
            </div>
          </q-item>
        </template>

        <!-- Analytics menu item - UPCOMING FEATURE -->
        <template v-if="isClient || isAdmin || isSubClient || isB2C">
          <q-item class="navDrawer__drawer-item navDrawer__drawer-item--disabled" @mouseover="showFeatureTooltip('analytics', $event)" @mouseleave="hideFeatureTooltip">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon class="navDrawer__disabled-icon" name="o_analytics" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="navDrawer__disabled-text" style="font-size: 16px">Analytics</q-item-label>
              </q-item-section>
            </div>
          </q-item>
        </template>

        <!-- Admin-only menu items -->
        <template v-if="isAdmin">
          <!-- Packages menu item -->
          <q-item class="navDrawer__drawer-item" clickable :active="isActive('packages')" @click.native="handleRoute('packages')">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon color="grey-7" name="inventory_2" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary" style="font-size: 16px">{{ $t('Packages') }}</q-item-label>
              </q-item-section>
            </div>
          </q-item>

          <!-- Accounts menu item -->
          <q-item class="navDrawer__drawer-item" clickable :active="isActive('accounts')" @click.native="handleRoute('accounts')">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon color="grey-7" name="o_manage_accounts" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary" style="font-size: 16px">{{ $t('Accounts') }}</q-item-label>
              </q-item-section>
            </div>
          </q-item>
        </template>

        <!-- CONNECT & SHARE Section - shown for all client types -->
        <template v-if="isClient || isB2C || isSubClient">
          <div class="section-header">CONNECT & SHARE</div>

          <!-- Integrations menu item -->
          <q-item class="navDrawer__drawer-item" clickable :active="isActive('integrations')" @click.native="handleRoute('integrations')">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon color="grey-7" name="o_settings_input_component" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary" style="font-size: 16px">{{ $t('Integrations Center') }}</q-item-label>
              </q-item-section>
            </div>
          </q-item>

          <!-- Publish to Platforms menu item -->
          <q-item class="navDrawer__drawer-item" clickable>
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon color="grey-7" name="o_publish" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary" style="font-size: 16px">Publish to Platforms</q-item-label>
              </q-item-section>
            </div>
          </q-item>

          <!-- Cloud Sync menu item - UPCOMING FEATURE -->
          <q-item class="navDrawer__drawer-item navDrawer__drawer-item--disabled" @mouseover="showFeatureTooltip('cloudSync', $event)" @mouseleave="hideFeatureTooltip">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon class="navDrawer__disabled-icon" name="o_cloud_sync" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="navDrawer__disabled-text" style="font-size: 16px">Cloud Sync</q-item-label>
              </q-item-section>
            </div>
          </q-item>
        </template>

        <!-- COLLABORATE Section - shown for all client types -->
        <template v-if="isClient || isB2C || isSubClient">
          <div class="section-header">COLLABORATE</div>

          <!-- Team Members menu item -->
          <q-item class="navDrawer__drawer-item" clickable :active="isActive('teams')" @click.native="handleRoute('teams')">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon color="grey-7" name="o_groups" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary" style="font-size: 16px">Teams</q-item-label>
              </q-item-section>
            </div>
          </q-item>

          <!-- Smart Workflows menu item - UPCOMING FEATURE -->
          <q-item class="navDrawer__drawer-item navDrawer__drawer-item--disabled" @mouseover="showFeatureTooltip('smartWorkflow', $event)" @mouseleave="hideFeatureTooltip">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon class="navDrawer__disabled-icon" name="o_account_tree" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="navDrawer__disabled-text" style="font-size: 16px">Smart Workflows</q-item-label>
              </q-item-section>
            </div>
          </q-item>
        </template>

        <!-- SETTINGS Section - shown for all users -->
        <template v-if="isClient || isB2C || isSubClient">
          <div class="section-header">SETTINGS</div>

          <!-- Account & Billing menu item -->
          <q-item class="navDrawer__drawer-item" clickable :active="isActive('personal-information')" @click.native="handleRoute('personal-information')">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon color="grey-7" name="o_person" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary" style="font-size: 16px">Personal Information</q-item-label>
              </q-item-section>
            </div>
          </q-item>

          <!-- Plan & Feature menu item -->
          <q-item class="navDrawer__drawer-item" clickable :active="isActive('billing-payments')" @click.native="handleRoute('billing-payments')">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon color="grey-7" name="o_credit_card" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary" style="font-size: 16px">Billing & Payments</q-item-label>
              </q-item-section>
            </div>
          </q-item>

          <!-- Security & Privacy menu item -->
          <q-item class="navDrawer__drawer-item" clickable :active="isActive('account-security')" @click.native="handleRoute('account-security')">
            <div class="navDrawer__content">
              <q-item-section avatar class="navDrawer__icon-container">
                <q-icon color="grey-7" name="o_security" :style="icon_style" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary" style="font-size: 16px">Account Security</q-item-label>
              </q-item-section>
            </div>
          </q-item>
        </template>
      </q-list>
    </q-scroll-area>

    <!-- Custom Navbar Tooltip -->
    <NavbarTooltip
      :feature-id="hoveredFeature"
      :name="hoveredFeature ? getFeatureName(hoveredFeature) : ''"
      :description="hoveredFeature ? getFeatureDescription(hoveredFeature) : ''"
      :is-coming-soon="isFeatureComingSoon(hoveredFeature)"
      :position-style="tooltipStyles"
    />
  </q-drawer>
</template>

<script>
// Vuex state management
import { mapGetters } from 'vuex';

// Utility mixins
import checkBreakpoint from '@utils/mixins/check-breakpoint.js';

// Components
import NavbarTooltip from './NavbarTooltip.vue';

export default {
  name: 'NavbarDrawer',
  mixins: [checkBreakpoint],
  components: {
    NavbarTooltip
  },
  // Component props
  props: {
    show_menu: {
      type: Boolean,
      default: false
    }
  },

  // Component data
  data() {
    return {
      version: process.env.VUE_APP_BUILD_NUMBER || 'local',
      // Icon styling
      icon_style: {
        fontSize: '25px'
      },
      showMenuLocal: this.show_menu,
      leftDrawerOpen: false,
      isNavigating: false, // Flag to prevent multiple rapid clicks
      // Tooltip functionality
      hoveredFeature: null,
      tooltipStyles: {
        top: '0px',
        left: '0px'
      }
    };
  },

  // Computed properties
  computed: {
    // Map Vuex getters for user roles
    ...mapGetters({
      isAdmin: 'user/isAdmin',
      isPhotographer: 'user/isPhotographer',
      isClient: 'user/isClient',
      isEditor: 'user/isEditor',
      isSubClient: 'user/isSubClient',
      isB2C: 'user/isB2C',
      isAgency: 'user/isAgency',
      hasShoot: 'user/hasShoot'
    }),

    // Simplified logic for who should see the dashboard
    shouldShowDashboard() {
      // Show dashboard for:
      // - Admins (always have dashboard access)
      // - Agency clients (they have dedicated dashboards)
      // - B2C users (individual customers)
      // - Sub-clients (clients under agencies)
      // Hide dashboard for:
      // - Regular non-agency clients (they use gallery as their main view)
      return this.isAdmin || (this.isClient && this.isAgency);
    },

    /**
     * Get appropriate label for orders/shoots menu item based on user role
     * @returns {string} Translated label text
     */
    getLabel() {
      if (this.isAdmin) return this.$t('ORDERS');
      return this.$t('SHOOTS');
    }
  },

  // Watch for prop changes
  watch: {
    show_menu(newVal) {
      this.showMenuLocal = newVal;
    }
  },

  // Lifecycle hooks
  mounted() {
    window.addEventListener('resize', this.handleWindowSize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleWindowSize);
  },

  // Component methods
  methods: {
    /**
     * Handle navigation to different routes
     * @param {string} type Route name to navigate to
     */
    handleRoute(type) {
      // Prevent multiple rapid clicks from causing navigation issues
      if (this.isNavigating) return;

      // Set navigating flag to true to prevent additional clicks
      this.isNavigating = true;

      // Check if we're already on this route to avoid redundant navigation
      if (this.$route.name === type) {
        // If already on the route, just reset the flag after a short delay
        setTimeout(() => {
          this.isNavigating = false;
        }, 300);
        return;
      }

      // Navigate to the new route with replace to avoid browser history stacking
      this.$router
        .replace({ name: type })
        .catch(err => {
          // Only log errors that aren't navigation duplications
          if (err.name !== 'NavigationDuplicated') {
            console.error('Navigation error:', err);
          }
        })
        .finally(() => {
          // Reset the navigation flag after a short delay
          setTimeout(() => {
            this.isNavigating = false;
          }, 300);
        });
    },

    /**
     * Check if given route name matches current route
     * @param {string} routeName Route name to check
     * @returns {boolean} Whether route is active
     */
    isActive(routeName) {
      return this.$route.name === routeName;
    },

    onDrawerToggle(value) {
      this.showMenuLocal = value;
      this.$emit('drawer-toggle', value);
      if (!value) {
        this.leftDrawerOpen = false;
        console.log('Drawer closed');
      } else {
        console.log('Drawer opened');
      }
    },

    closeDrawer() {
      this.showMenuLocal = false;
      this.$emit('drawer-toggle', false);
    },

    handleVideoGeneratorRoute() {
      // Prevent multiple rapid clicks from causing navigation issues
      if (this.isNavigating) return;

      // Set navigating flag to true to prevent additional clicks
      this.isNavigating = true;

      // Clear any existing video generator state since we're starting fresh
      this.$store.dispatch('videoGenerator/clearVideoGeneratorState');

      // Navigate to gallery with video-generate route
      this.$router
        .push({ path: '/gallery/video-generate' })
        .catch(err => {
          // Only log errors that aren't navigation duplications
          if (err.name !== 'NavigationDuplicated') {
            console.error('Navigation error:', err);
          }
        })
        .finally(() => {
          // Reset the navigation flag after a short delay
          setTimeout(() => {
            this.isNavigating = false;
          }, 300);
        });
    },

    handleAIEditorRoute() {
      // Prevent multiple rapid clicks from causing navigation issues
      if (this.isNavigating) return;

      // Set navigating flag to true to prevent additional clicks
      this.isNavigating = true;

      // Navigate to gallery with ai-editor route
      this.$router
        .push({ path: '/gallery/ai-editor' })
        .catch(err => {
          // Only log errors that aren't navigation duplications
          if (err.name !== 'NavigationDuplicated') {
            console.error('Navigation error:', err);
          }
        })
        .finally(() => {
          // Reset the navigation flag after a short delay
          setTimeout(() => {
            this.isNavigating = false;
          }, 300);
        });
    },

    // Tooltip methods for upcoming features
    showFeatureTooltip(featureId, event) {
      const itemRect = event.currentTarget.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const drawerElement = event.currentTarget.closest('.q-drawer');
      const drawerRect = drawerElement ? drawerElement.getBoundingClientRect() : null;

      const estimatedTooltipWidth = 350; // Max width of tooltip
      const estimatedTooltipHeight = 120; // Reduced height for simpler positioning
      const gap = 16; // Gap between drawer and tooltip

      // Calculate horizontal positioning - prioritize right side of drawer
      let leftPosition;

      if (drawerRect) {
        // Show tooltip to the right of the drawer
        leftPosition = drawerRect.right + gap;

        // If tooltip would overflow right edge, try left side
        if (leftPosition + estimatedTooltipWidth > windowWidth - 20) {
          leftPosition = drawerRect.left - estimatedTooltipWidth - gap;

          // If still overflowing (very narrow screen), center it
          if (leftPosition < 20) {
            leftPosition = Math.max(20, (windowWidth - estimatedTooltipWidth) / 2);
          }
        }
      } else {
        // Fallback positioning relative to item
        leftPosition = itemRect.right + gap;
        if (leftPosition + estimatedTooltipWidth > windowWidth - 20) {
          leftPosition = itemRect.left - estimatedTooltipWidth - gap;
        }
        if (leftPosition < 20) {
          leftPosition = 20;
        }
      }

      // Simplified vertical positioning - align with the menu item
      let topPosition;

      // Get all drawer items to check if this is the last one
      const allDrawerItems = document.querySelectorAll('.navDrawer__drawer-item');
      const currentItem = event.currentTarget;
      const isLastItem = currentItem === allDrawerItems[allDrawerItems.length - 1];

      if (isLastItem) {
        // For the last item, align tooltip with the item vertically
        topPosition = itemRect.top;

        // Ensure it doesn't go below viewport
        if (topPosition + estimatedTooltipHeight > windowHeight - 20) {
          topPosition = itemRect.top - estimatedTooltipHeight + itemRect.height;
        }
      } else {
        // For other items, center the tooltip on the item
        topPosition = itemRect.top + itemRect.height / 2 - estimatedTooltipHeight / 2;

        // Keep tooltip within viewport bounds
        if (topPosition < 20) {
          topPosition = 20;
        }
        if (topPosition + estimatedTooltipHeight > windowHeight - 20) {
          topPosition = windowHeight - estimatedTooltipHeight - 20;
        }
      }

      this.tooltipStyles = {
        top: `${topPosition}px`,
        left: `${leftPosition}px`
      };

      this.hoveredFeature = featureId;
    },

    hideFeatureTooltip() {
      this.hoveredFeature = null;
    },

    // Feature information methods
    getFeatureName(featureId) {
      const featureNames = {
        // Upcoming features only
        templates: 'Templates',
        collections: 'Collections',
        brandKits: 'Brand Kits',
        analytics: 'Analytics',
        smartWorkflow: 'Smart Workflows',
        publishToPlatform: 'Publish to Platforms',
        cloudSync: 'Cloud Sync'
      };

      return featureNames[featureId] || '';
    },

    getFeatureDescription(featureId) {
      const featureDescriptions = {
        // Upcoming features only - short single-line descriptions
        templates: 'Access pre-designed templates for your projects.',
        collections: 'Organize your content into custom collections. Access directly from the Smart Library.',
        brandKits: 'Manage your brand colors, fonts, and assets.',
        analytics: 'Get insights into your content performance.',
        smartWorkflow: 'Automate your creative processes with intelligent workflows.',
        publishToPlatform: 'Share your content across channels. Access directly from the Smart Library.',
        cloudSync: 'Sync your content across all your devices and platforms.'
      };

      return featureDescriptions[featureId] || '';
    },

    isFeatureComingSoon(featureId) {
      // All features in our system are coming soon
      const comingSoonFeatures = ['templates', 'brandKits', 'analytics', 'smartWorkflow', 'cloudSync'];

      return comingSoonFeatures.includes(featureId);
    }
  }
};
</script>

<style lang="scss" scoped>
/* Navigation drawer styling */
.navDrawer {
  &__list {
    padding: 16px 0;
  }

  &__drawer-item {
    line-height: 1.5rem;
    border-radius: 0 1.5rem 1.5rem 0;
    margin: 8px 16px;
    padding: 12px 24px;
    transition: all 0.2s ease;
    cursor: pointer;

    // Disabled state for upcoming features
    &--disabled {
      cursor: not-allowed;
      opacity: 0.7;

      &:hover {
        background-color: rgba(var(--secondary-text-color), 0.05) !important;
      }
    }
  }

  &__content {
    display: flex;
    align-items: center;
    width: 100%;

    .q-item-section {
      display: flex;
      align-items: center;
      justify-content: center;

      .q-item-label {
        @include inter-font(18px, true);
        color: var(--main-text-color);
      }
    }
  }

  &__drawer-item:hover:not(&__drawer-item--disabled) {
    background-color: rgba(254, 213, 89, 0.1);
  }

  &__drawer-item.q-item--active {
    background-color: rgba(254, 213, 89, 0.1);
    @include inter-font(16px, true);
    border-left: 4px solid #fed559;
    padding-left: 20px; // Compensate for border

    .q-icon,
    .q-item-label {
      color: var(--main-text-color) !important;
    }
  }

  .q-item-label:not(.q-item__label--caption) {
    @include mona-sans-font(14px, true);
    color: var(--main-text-color);
    letter-spacing: 0.018em;
    line-height: 1.25rem;
    white-space: nowrap;
  }

  &__icon-container {
    width: 32px !important;
    min-width: 32px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
  }

  &__disabled-text {
    @include inter-font($size: 14px, $bolded: false, $color: var(--secondary-text-color));
    color: var(--secondary-text-color) !important;
    letter-spacing: 0.018em;
    line-height: 1.25rem;
    white-space: nowrap;
    opacity: 0.8;
  }

  &__disabled-icon {
    color: var(--secondary-text-color) !important;
    opacity: 0.8;
  }
}

.drawer-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: inherit;
  display: flex;
  align-items: left;
  margin: 0;
  padding: 16px 12px;
  border-bottom: 1px solid rgba(151, 151, 151, 0.1);
}

.drawer-content {
  height: calc(100vh - 80px); /* Subtract header height */
}

.drawer-logo {
  height: 45px;
  margin-left: 24px;
}

.section-header {
  @include inter-font($size: 12px, $bolded: true, $color: var(--secondary-text-color), $weight: 600);
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 24px 40px 8px 40px;
  margin-top: 8px;
}
</style>
